// The security plane: `GET /v1/me` plus everything under `/api/cloud/auth/*`
// and the three framework-native auth routes (`06` §1.2, §3.1, §3.2).
//
// `/api/cloud/auth/*` sits deliberately OUTSIDE `/v1`. It is not a versioned
// client surface — it is the browser's own security plane, its shapes track the
// auth substrate rather than the product, and its absence from `/v1` is what
// makes "everything under `/v1` is in principle reachable by a scoped token
// carrying the right feature" a legible invariant instead of a list of
// exceptions (`06` §1.2). No `SC-F205` feature exists that could grant any of
// it, so a `sluice_ft_` token can never reach these routes at all.
//
// Four of the namespaces here are stricter still: `passkeys`, `verify`,
// `sign-out-all` and `events` require a *session* token and refuse a general
// `sluice_ak_` key (`01` §4.4.7 rows 57-60). A general key is a bearer secret
// living in CI configuration; letting it delete the account's passkeys or sign
// every device out converts a credential leak into an account takeover.

import type { RecordModel } from "pocketbase";
import { pb } from "./client.js";
import { ApiError, parseError } from "./errors.js";
import { del, getJSON, query, sendJSON, type Page, type PageParams } from "./http.js";
import type { Feature } from "./apiKeys.js";
import type { Role } from "./orgs.js";
import {
  credentialToJSON,
  parseCreationOptions,
  parseRequestOptions,
  type CreationOptionsWire,
  type RequestOptionsWire,
} from "./webauthn.js";

const AUTH = "/api/cloud/auth";

// ───────────────────────────────────────────────────────────── identity ──

/** CredentialKind is the class of credential the current request presented
 * (`06` §2.1). The UI reads it to decide what to hide: a general key cannot
 * manage passkeys, and a scoped token never receives audit record bodies. */
export type CredentialKind = "session" | "general" | "fine_grained" | "instance_agent";

/**
 * TokenScope is a fine-grained token's narrowing: the owner's authority
 * intersected with one organization, optionally one instance, and a feature set.
 * It is **null** for a session and for a general key — the nil scope means full
 * power and the scope check is skipped entirely (`SC-F206`, `SC-S211`).
 */
export interface TokenScope {
  org: string | null;
  instance: string | null;
  features: Feature[];
}

/** OrgMembership is one row of the caller's organization list, with the role
 * that decides what every screen in that organization may offer.
 *
 * `name` is the RFC-1123 label and `display_name` the prose, exactly as
 * `GET /v1/orgs` spells them — see the note on [Organization] in `orgs.ts` for
 * why that reads backwards and why it is right. */
export interface OrgMembership {
  id: string;
  name: string;
  display_name: string;
  role: Role;
}

/**
 * Me is the answer to `GET /v1/me` — the CLI's `whoami` and the SPA's boot call.
 * It is a `routePublic` route because it reveals only what the credential
 * already proves.
 */
export interface Me {
  account: { id: string; email: string; name: string };
  credential: {
    kind: CredentialKind;
    key_id?: string;
    display_prefix?: string;
    expires?: string | null;
  };
  scope: TokenScope | null;
  organizations: OrgMembership[];
  verification: { required_for: StepUpAction[] };
}

/** me returns the caller's identity, credential class and effective scope. Call
 * it once at boot: the shell derives the organization switcher, the role-gated
 * chrome and the "this view is redacted" affordances from it. */
export function me(): Promise<Me> {
  return getJSON<Me>("/v1/me");
}

/**
 * updateAccount sets the signed-in human's own profile — today only the display
 * name (`05` §3.1: `name`, ≤ 100 characters, "empty until onboarding
 * (`SC-F910`)"). It answers the updated account object, the same shape [Me]
 * carries.
 *
 * **This route is an addition, and the reasoning matters.** `05` §3.1 leaves
 * `users.UpdateRule` nil like every other collection (`06` §1.6 locks
 * auto-REST), so the framework-native self-update the reference implementation
 * used — `pb.collection("users").update(id, {name})` — is refused here by
 * design. `06` §3.1 then defines `GET /v1/me` and no writer for it, which leaves
 * onboarding with a field it is told to fill and no route to fill it with.
 * `PATCH` on the resource `GET /v1/me` already returns is the smallest thing
 * that closes that, and `06` §1.3 makes an added route additive.
 *
 * It is **session-only** and absent from the `SC-F205` route map, on the
 * precedent `POST /v1/invites/accept` sets one section later (`06` §3.3): a
 * `/v1` path may be session-only when the act is a person's rather than
 * automation's, and renaming a human is that. No feature in the coarse
 * `SC-F205` vocabulary describes "edit the account behind this credential", and
 * inventing one to let a CI token rename its owner would be a strange thing to
 * have built.
 */
export function updateAccount(input: { name: string }): Promise<Me["account"]> {
  return sendJSON<Me["account"]>("/v1/me", "PATCH", input);
}

// ─────────────────────────────────────────────────────────── sign-in ──

/**
 * requestOtp emails a one-time sign-in code and returns the id to pair with it.
 * There is no password anywhere in the product. This route **creates the account
 * on first request** (`SC-F001`, `SC-F002`), so the sign-in form is also the
 * sign-up form and must not promise otherwise.
 */
export async function requestOtp(email: string): Promise<{ otpId: string }> {
  const res = await pb.collection("users").requestOTP(email.trim());
  return { otpId: res.otpId };
}

/**
 * verifyOtp exchanges an otpId plus the emailed code for a 24-hour session
 * token, persisted into `pb.authStore` (which the app shell subscribes to). The
 * SDK passes the code in the `password` field.
 *
 * The bare 400 is re-tagged `otp_invalid` here rather than in the shared status
 * map, because only this route knows what a codeless 400 means: the auth
 * substrate answers wrong code, expired code and unknown otpId identically — a
 * deliberate anti-enumeration choice — and the `apierr` envelope is absent.
 * Without the re-tag a user who mistyped their code is told the server broke.
 */
export async function verifyOtp(otpId: string, code: string): Promise<void> {
  try {
    await pb.collection("users").authWithOTP(otpId, code.trim());
  } catch (err) {
    const parsed = parseError(err);
    if (parsed.status === 400 && parsed.code === "internal") {
      throw new ApiError({ code: "otp_invalid", status: 400, requestId: parsed.requestId });
    }
    throw err;
  }
}

/**
 * refreshSession slides the 24-hour session forward (`SC-F009`). The app shell
 * calls it once per boot: a fixed TTL would expire under an active user
 * mid-session, while refreshing at boot means anyone who opens the app daily
 * never sees the expiry and an idle-for-a-day session is genuinely gone.
 */
export async function refreshSession(): Promise<void> {
  await pb.collection("users").authRefresh();
}

/** signOut clears the persisted session locally. The shell reacts through
 * `authStore.onChange`. */
export function signOut(): void {
  pb.authStore.clear();
}

/**
 * signOutEverywhere revokes every session for the caller server-side, including
 * this one and any CLI tokens exchanged from API keys (`SC-F010`). It does NOT
 * clear the local store — the caller does that with [signOut] once the server
 * confirms, so a failed request leaves the user signed in rather than stranded
 * with a cleared store and live sessions elsewhere.
 */
export async function signOutEverywhere(): Promise<void> {
  await sendJSON<unknown>(`${AUTH}/sign-out-all`, "POST", {});
}

/** currentUser is the cached auth record, or null when signed out. */
export function currentUser(): { id: string; email: string; name: string } | null {
  const r = pb.authStore.record;
  if (!r) return null;
  return {
    id: r.id,
    email: (r.email as string | undefined) ?? "",
    name: (r.name as string | undefined) ?? "",
  };
}

// ──────────────────────────────────────────────────────────── passkeys ──

/** Passkey is one registered WebAuthn credential (`SC-F007`). */
export interface Passkey {
  id: string;
  name: string;
  created: string;
  /** Empty string when never used. */
  lastUsed: string;
  /** True when the authenticator reports the credential as backed up (the
   * WebAuthn BS flag) — it syncs via iCloud Keychain, Google Password Manager
   * and the like. False means it lives on one device only. */
  backedUp: boolean;
}

/** listPasskeys returns the account's registered passkeys, newest first. */
export async function listPasskeys(): Promise<Passkey[]> {
  const { items } = await getJSON<{ items: Passkey[] }>(`${AUTH}/passkeys`);
  return items;
}

/** renamePasskey updates one passkey's display name. */
export async function renamePasskey(id: string, name: string): Promise<void> {
  await sendJSON<unknown>(`${AUTH}/passkeys/${encodeURIComponent(id)}`, "PATCH", { name });
}

/**
 * deletePasskey removes a passkey — **including the last one**, by design
 * (`SC-F007`). The email-code flow always remains, so there is no lockout to
 * guard against and refusing would be dishonest about where the real recovery
 * path is.
 */
export async function deletePasskey(id: string): Promise<void> {
  await del(`${AUTH}/passkeys/${encodeURIComponent(id)}`);
}

/**
 * registerPasskey runs the full registration ceremony for the signed-in user:
 * begin → `navigator.credentials.create` (the browser prompts for fingerprint,
 * face or PIN) → finish, which stores the credential under `name`.
 *
 * Rejects with a DOMException when the user dismisses the browser prompt;
 * callers pass that through `passkeyFailureMessage`, which returns null for a
 * cancel. Reload the list afterwards rather than trusting a response echo.
 */
export async function registerPasskey(name: string): Promise<void> {
  // Reject an over-long name BEFORE the browser prompt. The server enforces the
  // limit only at finish, by which point the authenticator has already minted a
  // resident credential — a phantom passkey the user must clean up in their
  // password manager.
  if (name.length > 100) {
    throw new ApiError({
      code: "validation_failed",
      message: "Passkey names are limited to 100 characters.",
    });
  }
  const { sessionId, options } = await sendJSON<{
    sessionId: string;
    options: CreationOptionsWire;
  }>(`${AUTH}/passkeys/register/begin`, "POST", {});
  const cred = await navigator.credentials.create({ publicKey: parseCreationOptions(options) });
  if (!cred) throw new ApiError({ code: "internal", message: "The passkey could not be created." });
  await sendJSON<unknown>(`${AUTH}/passkeys/register/finish`, "POST", {
    sessionId,
    name,
    credential: credentialToJSON(cred as PublicKeyCredential),
  });
}

/** PasskeyLoginOptions controls the discoverable-credential sign-in ceremony. */
export interface PasskeyLoginOptions {
  /** Run as conditional mediation: the browser offers passkeys in the email
   * field's autofill instead of opening a modal, and the promise stays pending
   * until the user picks one (or `signal` aborts it). */
  conditional?: boolean;
  /** Aborts a pending ceremony — required bookkeeping for conditional runs. */
  signal?: AbortSignal;
}

/**
 * loginWithPasskey runs the discoverable-credential login ceremony and, on
 * success, saves the minted session into `pb.authStore` — the same end state as
 * the OTP flow. Returns the auth record so the caller can branch on it (an empty
 * `name` routes a fresh account to onboarding).
 *
 * The begin/finish pair is deliberately **anonymous**: it must work with no
 * session, and attaching a stale stored token would produce 401s unrelated to
 * the passkey itself.
 */
export async function loginWithPasskey(opts: PasskeyLoginOptions = {}): Promise<RecordModel> {
  const begin = await sendJSON<{ sessionId: string; options: RequestOptionsWire }>(
    `${AUTH}/passkeys/login/begin`,
    "POST",
    {},
    { anonymous: true },
  );
  const cred = await navigator.credentials.get({
    publicKey: parseRequestOptions(begin.options),
    mediation: opts.conditional ? "conditional" : undefined,
    signal: opts.signal,
  });
  if (!cred) throw new ApiError({ code: "unauthorized" });
  const res = await sendJSON<{ token: string; record: RecordModel }>(
    `${AUTH}/passkeys/login/finish`,
    "POST",
    { sessionId: begin.sessionId, credential: credentialToJSON(cred as PublicKeyCredential) },
    { anonymous: true },
  );
  pb.authStore.save(res.token, res.record);
  return res.record;
}

// ────────────────────────────────────────────────────── step-up grants ──

/**
 * StepUpAction names what a verification grant will authorize (`01` §5.4). The
 * ids must match the Go vocabulary exactly; the server rejects anything else.
 *
 * Grants carry a **target** as well as an action, which is the deviation from
 * bino-cloud worth knowing about: a grant that authorizes "delete *some*
 * instance" is a grant an attacker can redirect while the user is looking at the
 * confirmation dialog for a different one. Actions that name no resource
 * (`create_api_key`) leave the target empty.
 */
export type StepUpAction =
  | "create_api_key"
  | "delete_instance"
  | "rotate_credential"
  | "export_audit"
  | "change_owner"
  | "publish_widening"
  | "impersonate_subject"
  // `SC-F810`. Approving a held query releases it against production data and the
  // grant Sluice mints for it is spent by that query within `approval.grantTtl`,
  // so there is no undo. Its opposite is deliberately absent: rejecting reduces
  // authority and needs no grant, which is what keeps the safe answer the quick
  // one.
  | "approve_query";

/** StepUpTarget binds a grant to the resource the dialog is about. Omit it for
 * the actions the parse table declares target-less. */
export interface StepUpTarget {
  action: StepUpAction;
  /** Record id of the instance, organization, datasource or secret in question. */
  target?: string;
}

// The server decodes `target` as `{kind, id}` and the kind follows from the action.
const TARGET_KIND: Partial<Record<StepUpAction, string>> = {
  delete_instance: "instance",
  export_audit: "instance",
  publish_widening: "instance",
  impersonate_subject: "instance",
  approve_query: "instance",
  change_owner: "organization",
};

function stepUpBody(t: StepUpTarget) {
  if (!t.target) return { action: t.action };
  return { action: t.action, target: { kind: TARGET_KIND[t.action] ?? "", id: t.target } };
}

/** requestStepUpOtp emails the signed-in user a one-time code and returns the id
 * to confirm it with (`SC-F011`). */
export function requestStepUpOtp(): Promise<{ otpId: string }> {
  return sendJSON<{ otpId: string }>(`${AUTH}/verify/otp/request`, "POST", {});
}

/**
 * confirmStepUpOtp redeems an emailed code for a verification grant. The server
 * answers a uniform `otp_invalid` for wrong, expired and foreign codes alike, so
 * callers have exactly one failure path.
 *
 * The returned grant is single-use with a five-minute TTL and is spent by
 * passing it as `verification` on the next write.
 */
export async function confirmStepUpOtp(
  otpId: string,
  code: string,
  target: StepUpTarget,
): Promise<string> {
  const res = await sendJSON<{ verification: string }>(`${AUTH}/verify/otp/confirm`, "POST", {
    otpId,
    code,
    ...stepUpBody(target),
  });
  return res.verification;
}

/**
 * verifyWithPasskey runs the step-up assertion ceremony for the signed-in user
 * and returns the grant. Unlike [loginWithPasskey] it never touches
 * `pb.authStore`: a step-up assertion earns a grant, it does not mint or replace
 * the session.
 *
 *   const grant = await verifyWithPasskey({ action: "delete_instance", target: id });
 *   await deleteInstance(id, { verification: grant });
 */
export async function verifyWithPasskey(target: StepUpTarget): Promise<string> {
  const begin = await sendJSON<{ sessionId: string; options: RequestOptionsWire }>(
    `${AUTH}/verify/passkey/begin`,
    "POST",
    {},
  );
  const cred = await navigator.credentials.get({ publicKey: parseRequestOptions(begin.options) });
  if (!cred) throw new ApiError({ code: "verification_invalid" });
  const res = await sendJSON<{ verification: string }>(`${AUTH}/verify/passkey/finish`, "POST", {
    sessionId: begin.sessionId,
    credential: credentialToJSON(cred as PublicKeyCredential),
    ...stepUpBody(target),
  });
  return res.verification;
}

// ────────────────────────────────────────────────── sign-in history ──

/**
 * SecurityEvent is one row of the account's own sign-in and security history
 * (`SC-F016`). It sits on the security plane rather than at `GET /v1/audit`
 * deliberately: `SC-F205` has no feature for security management, so no scoped
 * token may read a human's authentication history.
 *
 * TODO(SC-F016): the field set is defined from `01` §4.6; the Go handler does
 * not exist yet. Treat additions as additive per `06` §1.3.
 */
export interface SecurityEvent {
  id: string;
  /** `sign_in`, `sign_out_all`, `passkey_added`, `passkey_removed`,
   * `api_key_created`, `api_key_revoked`, `step_up_verified`, … */
  event: string;
  created: string;
  /** How the caller authenticated for this event, when the event has a method. */
  method?: "otp" | "passkey" | "sso" | "api_key";
  ip?: string;
  user_agent?: string;
  /** False for a failed attempt; a failed sign-in is history too. */
  ok: boolean;
}

/** listSecurityEvents returns the account's own security history, newest first,
 * keyset-paginated like every other list route. */
export function listSecurityEvents(params: PageParams = {}): Promise<Page<SecurityEvent>> {
  return getJSON<Page<SecurityEvent>>(`${AUTH}/events${query({ ...params })}`);
}
